import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {IFile} from "../../types/gpsr-questions";
import {startLoading, stopLoading} from "./loadingSlice";
import {GpsrService} from "../../api/generated/services/GpsrService";
import {GpsrCustomService} from "../../api/customServices/GpsrCustomService";
import type {GpsrProductView} from "../../api/generated/models/GpsrProductView";
import type {GpsrUpdateProductRequest} from "../../api/generated/models/GpsrUpdateProductRequest";
import type {GpsrRiskAnswerView} from "../../api/generated/models/GpsrRiskAnswerView";

interface ISetQuestionData {
    topicID: string;
    questionID: string;
    pointID: string;
    textarea: string;
}

interface ISetQuestionFiles {
    topicID: string;
    questionID: string;
    pointID: string;
    files: IFile[];
}

interface IDeleteFile {
    topicID: string;
    questionID: string;
    pointID: string;
    file: IFile;
}

export interface IQuestion {
    topicId: string;
    questionId: string;
    pointId: string;
    textarea?: string;
    files: IFile[];
}

interface IGpsrQuestionsListState {
    riskQuestions: IQuestion[];
}

interface IUploadFilesResponse {
    status?: string;
    files?: IFile[];
}

interface IUpdateProductResponse {
    status?: string;
    message?: string;
}

interface IDeleteFileResponse {
    status?: string;
    message?: string;
}

const initialState: IGpsrQuestionsListState = {
    riskQuestions: [],
};

export const getProduct = createAsyncThunk(
    "questionsList/getProduct",
    async (productID: string, {dispatch}) => {
        dispatch(startLoading());

        try {
            const response: GpsrProductView = await GpsrService.getAppGpsrProductGet(productID);
            return response;
        } catch (err) {
            dispatch(stopLoading());
            throw err;
        } finally {
            dispatch(stopLoading());
        }
    }
);

export const updateProduct = createAsyncThunk(
    "questionsList/updateProduct",
    async (data: GpsrUpdateProductRequest, {dispatch}) => {
        dispatch(startLoading());

        try {
            const response = await GpsrService.putAppGpsrProductUpdate(data);
            dispatch(stopLoading());
            return { status: "success", message: response.message };
        } catch (err) {
            dispatch(stopLoading());
            throw err;
        } finally {
            dispatch(stopLoading());
        }
    }
);

export const uploadFiles = createAsyncThunk(
    "questionsList/uploadFiles",
    async (data: { files: FileList, topicID: string, questionID: string, pointID: string }, {dispatch}) => {
        try {
            dispatch(startLoading());

            const {files, topicID, questionID, pointID} = data;

            const response = await GpsrCustomService.gpsrProductUploadFiles(
                Array.from(files)
            );

            const uploadedFiles: IFile[] = (response.files || []).map((f: { name?: string; originalName?: string }) => ({
                name: f.name || '',
                originalName: f.originalName || ''
            }));

            if (uploadedFiles.length > 0) {
                dispatch(
                    setQuestionFiles({topicID, questionID, pointID, files: uploadedFiles})
                );
            }

            return { status: "success", files: uploadedFiles };

        } catch (e) {
            dispatch(stopLoading());
            console.error("Upload error:", e);
            throw e;
        } finally {
            dispatch(stopLoading());
        }
    }
);

export const deleteFileThunk = createAsyncThunk(
    "questionsList/deleteFileThunk",
    async (fileName: string, {dispatch}) => {
        dispatch(startLoading());

        try {
            const response = await GpsrService.deleteAppGpsrFileDelete(fileName);
            return { status: "success", message: response.message };
        } catch (err) {
            dispatch(stopLoading());
            throw err;
        } finally {
            dispatch(stopLoading());
        }
    }
);

export const gpsrQuestionsListSlice = createSlice({
    name: "questionsList",
    initialState,
    reducers: {
        setQuestionData(state, action: PayloadAction<ISetQuestionData>) {
            const {topicID, questionID, pointID, textarea} = action.payload;
            let found: boolean = false,
                questionIndex: number = -1;

            state.riskQuestions.map((question: IQuestion, index) => {
                if (
                    question.topicId === topicID &&
                    question.questionId === questionID &&
                    question.pointId === pointID
                ) {
                    question.textarea = textarea;
                    found = true;

                    if (question.files.length === 0 && question.textarea === "")
                        questionIndex = index;
                }

                return question;
            });

            if (!found) {
                state.riskQuestions.push({
                    topicId: topicID,
                    questionId: questionID,
                    pointId: pointID,
                    textarea,
                    files: []
                });
            }

            if (questionIndex !== -1) state.riskQuestions.splice(questionIndex, 1);
        },

        setQuestionFiles(state, action: PayloadAction<ISetQuestionFiles>) {
            const {topicID, questionID, pointID, files} = action.payload;
            let found = false;

            if (files.length > 0) {
                state.riskQuestions.map((question: IQuestion) => {
                    if (
                        question.topicId === topicID &&
                        question.questionId === questionID &&
                        question.pointId === pointID
                    ) {
                        for (let i = 0; i < files.length; i++) {
                            question.files.push({
                                name: files[i].name,
                                originalName: files[i].originalName,
                            });
                        }
                        found = true;
                    }

                    return question;
                });

                if (!found) {
                    const filesArr: IFile[] = [];

                    for (let i = 0; i < files.length; i++) {
                        filesArr.push({name: files[i].name, originalName: files[i].originalName});
                    }

                    state.riskQuestions.push({
                        topicId: topicID,
                        questionId: questionID,
                        pointId: pointID,
                        files: filesArr
                    });
                }
            }
        },

        deleteFile(state, action: PayloadAction<IDeleteFile>) {
            const {topicID, questionID, pointID, file} = action.payload;
            let questionIndex: number = -1;

            state.riskQuestions.map((question: IQuestion, index) => {
                if (
                    question.topicId === topicID &&
                    question.questionId === questionID &&
                    question.pointId === pointID
                ) {
                    question.files = question.files.filter(
                        (pFile) => pFile.originalName !== file.originalName
                    );
                    if (question.files.length === 0 && question.textarea === "")
                        questionIndex = index;
                }
            });

            if (questionIndex !== -1) state.riskQuestions.splice(questionIndex, 1);
        },

        resetRiskQuestions(state) {
            state.riskQuestions = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(uploadFiles.fulfilled, (state, action) => {
            })
            .addCase(getProduct.fulfilled, (state, action) => {
                const {riskQuestions} = action.payload;

                if (riskQuestions && riskQuestions.length > 0) {
                    riskQuestions.forEach((item: GpsrRiskAnswerView) => {
                        const filesArr: IFile[] = [];
                        if (item.files && item.files.length > 0) {
                            item.files.forEach((file) => {
                                filesArr.push({name: file.name || "", originalName: file.originalName || ""});
                            });
                        }

                        const data: IQuestion = {
                            topicId: item.topicId || "",
                            questionId: item.questionId || "",
                            pointId: item.pointId || "",
                            textarea: item.textarea || "",
                            files: filesArr,
                        };

                        state.riskQuestions.push(data);
                    });
                }
            })
            .addCase(updateProduct.fulfilled, (state, action) => {});
    },
});

export const {setQuestionData, setQuestionFiles, deleteFile, resetRiskQuestions} =
    gpsrQuestionsListSlice.actions;
export default gpsrQuestionsListSlice.reducer;
